import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

const backendUrl = CommonHelperFunctions.GetBackendUrl();

export interface BlockModel {
  id: string;
  name: string;
  category: string;
  screenshot?: string;
}

export interface GetBlocksListResponse {
  blocks: BlockModel[];
}

export class BlocksService {
  // Simple in-memory cache for blob URLs
  private static screenshotCache: Map<string, string> = new Map();

  private static jsonCache: Map<string, string> = new Map();

  private static maxConcurrent = 10;

  private static activeCount = 0;

  private static queue: Array<() => void> = [];

  // Removed IndexedDB persistence and JSON hashing

  private static withConcurrency<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const run = async () => {
        try {
          BlocksService.activeCount++;
          const result = await fn();
          resolve(result);
        } catch (err) {
          reject(err);
        } finally {
          BlocksService.activeCount--;
          const next = BlocksService.queue.shift();
          if (next) {
            next();
          }
        }
      };

      if (BlocksService.activeCount < BlocksService.maxConcurrent) {
        run();
      } else {
        BlocksService.queue.push(run);
      }
    });
  }

  public static async GetBlocksList(): Promise<AxiosResponse<GetBlocksListResponse>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/blocks/list`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetBlockScreenshotFromList(block: BlockModel): Promise<string> {
    const cached = this.screenshotCache.get(block.id);
    if (cached) return cached;

    if (block.screenshot) {
      const blobUrl = this.base64ToBlobUrl(block.screenshot, "image/avif");
      this.screenshotCache.set(block.id, blobUrl);
      return blobUrl;
    }

    // Fallback to endpoint if screenshot not included
    return this.GetBlockScreenshot(block.id);
  }

  private static base64ToBlobUrl(base64: string, mime: string): string {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime });
    return URL.createObjectURL(blob);
  }

  public static async GetBlockScreenshot(blockId: string): Promise<string> {
    try {
      const mem = this.screenshotCache.get(blockId);
      if (mem) return mem;

      const response = await this.withConcurrency(() =>
        Axios.get(`${backendUrl}/blocks/screenshot/${blockId}`, { responseType: "blob" })
      );

      const blob: Blob = response.data;
      const blobUrl = URL.createObjectURL(blob);
      this.screenshotCache.set(blockId, blobUrl);
      return blobUrl;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  public static GetBlockScreenshotLink(blockId: string): string {
    return `${backendUrl}/blocks/screenshot/${blockId}`;
  }

  // Deprecated: prefer GetBlockJsonBatched
  public static async GetBlockJson(blockId: string): Promise<string> {
    return this.GetBlockJsonBatched(blockId);
  }

  // --- Batched JSON fetching ---
  private static pendingIds: Set<string> = new Set();

  private static pendingResolvers: Map<string, Array<(value: string) => void>> = new Map();

  private static pendingRejecters: Map<string, Array<(reason?: any) => void>> = new Map();

  private static flushTimer: any = null;

  public static async GetBlockJsonBatched(blockId: string): Promise<string> {
    if (this.jsonCache.has(blockId)) return this.jsonCache.get(blockId)!;
    return new Promise<string>((resolve, reject) => {
      const resArr = this.pendingResolvers.get(blockId) || [];
      const rejArr = this.pendingRejecters.get(blockId) || [];
      resArr.push(resolve);
      rejArr.push(reject);
      this.pendingResolvers.set(blockId, resArr);
      this.pendingRejecters.set(blockId, rejArr);
      this.pendingIds.add(blockId);
      this.scheduleFlush();
    });
  }

  private static scheduleFlush() {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flushBatch().catch(() => {});
    }, 20);
  }

  private static async flushBatch() {
    while (this.pendingIds.size > 0) {
      const ids = Array.from(this.pendingIds).slice(0, 8);
      ids.forEach((id) => this.pendingIds.delete(id));
      try {
        const response = await this.withConcurrency(() => Axios.post(`${backendUrl}/blocks/json/batch`, ids));
        const data = (response.data || {}) as Record<string, string>;
        ids.forEach((id) => {
          const json = data[id];
          const resolvers = this.pendingResolvers.get(id) || [];
          const rejecters = this.pendingRejecters.get(id) || [];
          if (json !== undefined) {
            const payload = typeof json === "string" ? json : JSON.stringify(json);
            this.jsonCache.set(id, payload);
            resolvers.forEach((r) => r(payload));
          } else {
            rejecters.forEach((r) => r(new Error(`Block json not found for id ${id}`)));
          }
          this.pendingResolvers.delete(id);
          this.pendingRejecters.delete(id);
        });
      } catch (error) {
        ids.forEach((id) => {
          const rejecters = this.pendingRejecters.get(id) || [];
          rejecters.forEach((r) => r(error));
          this.pendingResolvers.delete(id);
          this.pendingRejecters.delete(id);
        });
        throw error;
      }
    }
  }

  public static async GetBlocksJsonBatch(ids: string[]): Promise<Record<string, string>> {
    try {
      const missing = ids.filter((id) => !this.jsonCache.has(id));
      const result: Record<string, string> = {};
      // Already cached
      ids.forEach((id) => {
        const v = this.jsonCache.get(id);
        if (v) result[id] = v;
      });
      if (missing.length === 0) return result;
      const response = await this.withConcurrency(() => Axios.post(`${backendUrl}/blocks/json/batch`, missing));
      const data = response.data as Record<string, string>;
      Object.keys(data || {}).forEach((id) => {
        const json = typeof data[id] === "string" ? data[id] : JSON.stringify(data[id]);
        this.jsonCache.set(id, json);
        result[id] = json;
      });
      return result;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  public static async PreloadBlockJson(blockId: string): Promise<void> {
    try {
      if (this.jsonCache.has(blockId)) return;
      await this.GetBlockJsonBatched(blockId);
    } catch {}
  }

  public static clearScreenshotCache(): void {
    // Revoke all blob URLs to free memory
    this.screenshotCache.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    this.screenshotCache.clear();
    this.jsonCache.clear();
  }

  public static createCustomBlock(customBlockName: string, json: string, usedStyles?: string): Promise<AxiosResponse> {
    return Axios.post(`${backendUrl}/blocks/create-custom-block`, { customBlockName, json, usedStyles });
  }

  public static DeleteBlock(blockId: string): Promise<AxiosResponse> {
    return Axios.delete(`${backendUrl}/blocks/delete/${blockId}`);
  }
}
